/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { Link } from '@/components/link';
import { type LinkValue, resolveLinkHref } from '@/lib/link/link-picker';
import type { LinkPageData } from '@/lib/link/link-picker.server';

/* v8 ignore start - decorators are exercised by the generate-cartridge step, not unit tests */
@Component('footerLink', {
    name: 'Footer Link',
    description: 'A single footer link (label + destination). Used in the navigation and legal regions.',
    group: '4_BlackMagic_Footer',
})
@RegionDefinition([])
export class FooterLinkMetadata {
    @AttributeDefinition({ id: 'label', name: 'Label', type: 'string', required: true })
    label?: string;

    // Uses the custom link picker (not the built-in `type: 'url'` editor) so Product/Category/Page links
    // resolve on this single-site storefront. Not `required` — Page Designer can't run its required-check
    // against a custom object value and fails the component's Apply. Legacy string URLs still render.
    @AttributeDefinition({
        id: 'url',
        name: 'Link',
        type: 'custom',
        // Inline literal (not the shared const) — the metadata generator only resolves same-file literals.
        editorDefinition: { type: 'breakout.linkPicker' },
        description:
            'Pick a Product, Category, or Learn article (Page), or enter a URL (an internal path or https://…).',
    })
    url?: LinkValue;

    @AttributeDefinition({
        id: 'newTab',
        name: 'External Link',
        type: 'boolean',
        required: false,
        description: 'When checked, the link opens in a new browser tab.',
    })
    newTab?: boolean;
}
/* v8 ignore stop */

// Font size is inherited from the region container so the same link works at nav size and legal size.
const linkClass = 'inline-block transition-opacity hover:opacity-70';

export default function FooterLink({
    label,
    url,
    newTab,
    data,
}: {
    label?: string;
    url?: LinkValue;
    newTab?: boolean;
    data?: LinkPageData;
}): ReactElement | null {
    if (!label?.trim()) return null;
    const { href, external } = resolveLinkHref(url, data);

    // "External Link" checked → open in a new tab. A new tab is a fresh document load, so client-side
    // routing doesn't apply: render a plain anchor with target/rel regardless of internal/external.
    if (newTab) {
        return (
            <a href={href} target="_blank" rel="noopener noreferrer" className={linkClass}>
                {label}
            </a>
        );
    }

    return external ? (
        <a href={href} className={linkClass}>
            {label}
        </a>
    ) : (
        <Link to={href} className={linkClass}>
            {label}
        </Link>
    );
}

// eslint-disable-next-line react-refresh/only-export-components -- loader re-export required for the static-registry AST plugin
export { loader } from './footer-link.loaders';
